import { AxiosRequestConfig, AxiosResponse } from 'axios'

import { CONFIGS } from '../../config'
import type { IAuthorizeRequestData, IProfileResponse } from '../../types/api/auth.d'
import type { IAxiosResponseData } from '../../types/api/axiosResponse.d'
import { isBrowser } from '../../utils/isBrowser'
import { storage } from '../../utils/storage'
import { publicRequest } from './publicRequest'

export const authorize = async (
  data: IAuthorizeRequestData
): Promise<IProfileResponse> => {
  if (!isBrowser) {
    // Step 1 — get authorization code
    const step1 = new FormData()
    step1.append('email', data.email)
    step1.append('password', data.password)

    const authResponse: AxiosResponse<any, AxiosRequestConfig> =
      await publicRequest.post(
        `v1/oauth/authorize-rn?client_id=${CONFIGS.CLIENT_ID}`,
        step1,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      )

    const code: string | undefined = authResponse.data?.data?.code
    if (!code) throw new Error('Authorization code not received')

    // Step 2 — exchange code for access token
    const step2 = new FormData()
    step2.append('code', code)
    step2.append('scope', 'profile')
    step2.append('grant_type', 'authorization_code')
    step2.append('client_id', CONFIGS.CLIENT_ID)
    step2.append('client_secret', CONFIGS.CLIENT_SECRET)

    const tokenResponse: AxiosResponse<any, AxiosRequestConfig> =
      await publicRequest.post('v1/oauth/access_token', step2, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

    const accessToken: string | undefined = tokenResponse.data?.access_token
    const refreshToken: string | undefined = tokenResponse.data?.refresh_token

    if (accessToken) {
      storage.setItem('access_token', accessToken)
      publicRequest.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
    }
    if (refreshToken) {
      storage.setItem('refresh_token', refreshToken)
    }

    // Step 3 — fetch profile with Bearer token
    return getProfileData()
  }

  // Web flow
  const response: AxiosResponse<IProfileResponse, AxiosRequestConfig> =
    await publicRequest.post(
      `/auth?clientId=${CONFIGS.CLIENT_ID}`,
      data,
      { withCredentials: true }
    )
  return response.data
}

export const refreshAccessToken = async (): Promise<void> => {
  const refreshToken = storage.getItem('refresh_token')
  if (!refreshToken) throw new Error('No refresh token stored')

  const body = new FormData()
  body.append('grant_type', 'refresh_token')
  body.append('client_id', CONFIGS.CLIENT_ID)
  body.append('client_secret', CONFIGS.CLIENT_SECRET)
  body.append('refresh_token', refreshToken)

  const tokenResponse: AxiosResponse<any, AxiosRequestConfig> =
    await publicRequest.post('v1/oauth/access_token', body, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })

  const accessToken: string | undefined = tokenResponse.data?.access_token
  const newRefreshToken: string | undefined = tokenResponse.data?.refresh_token

  if (accessToken) {
    storage.setItem('access_token', accessToken)
    publicRequest.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
  }
  if (newRefreshToken) {
    storage.setItem('refresh_token', newRefreshToken)
  }
}

export const register = async (data: FormData): Promise<IProfileResponse> => {
  const response: AxiosResponse<IProfileResponse, AxiosRequestConfig> =
    await publicRequest.post('v1/oauth/register-rn', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  return response.data
}

export const getProfileData = async (): Promise<IProfileResponse> => {
  const response: AxiosResponse<IProfileResponse, AxiosRequestConfig> =
    await publicRequest.get('/customer/profile/', { withCredentials: isBrowser })
  return response.data
}

export const logout = async (): Promise<IAxiosResponseData> => {
  storage.removeItem('access_token')
  storage.removeItem('refresh_token')
  delete publicRequest.defaults.headers.common['Authorization']

  if (isBrowser) {
    const response: AxiosResponse<IAxiosResponseData, AxiosRequestConfig> =
      await publicRequest.delete('/auth', { withCredentials: true })
    return response.data
  }

  return { success: true } as IAxiosResponseData
}

/**
 * Checks whether a given email address already exists via the `/ajax/contact-email` endpoint.
 *
 * The underlying API is expected to return a JSON object containing:
 * - `exists`: `1` if the email exists, `0` otherwise
 * - `error`: `1` if an error occurred, `0` otherwise
 * - `message`: an optional error message when `error === 1`
 *
 * This function normalizes that response to an object with:
 * - `exists`: a boolean indicating whether the email exists
 * - `error`: an optional string containing an error message, if any
 *
 * On network or unexpected errors, it returns `{ exists: false, error: 'Failed to check email' }`.
 *
 * @param {string} email - The email address to check for existence.
 * @returns {Promise<{ exists: boolean; error?: string }>} A promise that resolves to the normalized
 *          result of the email existence check.
 */
export const checkEmailExists = async (
  email: string
): Promise<{ exists: boolean; error?: string }> => {
  try {
    const formData = new FormData()
    formData.append('email', email)
    formData.append('is_checkout_flow', 'true')

    const baseUrl =
      CONFIGS.BASE_URL || publicRequest.defaults.baseURL?.replace('/api', '') || ''
    const url = `${baseUrl}/ajax/contact-email`

    const response: AxiosResponse<any, AxiosRequestConfig> = await publicRequest.post(
      url,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    )

    return {
      exists: response.data.exists === 1,
      error: response.data.error === 1 ? response.data.message : undefined,
    }
  } catch (error) {
    console.error('Error checking email:', error)
    return {
      exists: false,
      error: 'Failed to check email',
    }
  }
}
